import { useState } from 'react';
import { Link } from 'react-router-dom';
import { projects, type ProjectType, type ProjectCategory } from '../../../mocks/references';
import BeforeAfterSlider from '../../../components/base/BeforeAfterSlider';

type FilterType = 'all' | ProjectType | ProjectCategory;

const typeFilters: { label: string; value: FilterType }[] = [
  { label: 'Všetky', value: 'all' },
  { label: 'Webová stránka', value: 'Webová stránka' },
  { label: 'Firemný web', value: 'Firemný web' },
  { label: 'CRM & Systémy', value: 'CRM & Systémy' },
  { label: 'Osobný brand', value: 'Osobný brand' },
  { label: 'Redesign', value: 'redesign' },
];

export default function RefGrid() {
  const [active, setActive] = useState<FilterType>('all');

  const filtered = projects.filter((p) => {
    if (active === 'all') return true;
    if (active === 'od-nuly' || active === 'redesign') return p.type === active;
    return p.category === active || p.category.includes(active as string);
  });

  return (
    <section className="py-16 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">
        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-12">
          {typeFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => setActive(f.value)}
              className={`text-sm font-medium px-4 py-2 rounded-full border transition-all duration-200 whitespace-nowrap cursor-pointer ${
                active === f.value
                  ? 'bg-orange-500 border-orange-500 text-white'
                  : 'bg-transparent border-zinc-700/60 text-zinc-400 hover:border-zinc-600 hover:text-white'
              }`}
            >
              {f.label}
              {f.value === 'all' && (
                <span className="ml-2 text-xs opacity-60">{projects.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-5 mb-8">
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
            <span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
            Od nuly - projekt postavený na zelenej lúke
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
            <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
            Redesign - potiahnite slider pre porovnanie pred/po
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
            <i className="ri-user-line text-zinc-600 text-xs" />
            Každý projekt som robil osobne - Kamil Frajtko
          </span>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filtered.map((project) => (
            <article
              key={project.id}
              className="group bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800/60 hover:border-orange-500/25 transition-all duration-300"
            >
              {/* Visual */}
              <div className="overflow-hidden relative bg-zinc-800">
                {project.type === 'redesign' && project.beforeImage && project.afterImage ? (
                  <div className="p-3 pt-0">
                    {/* Browser chrome for redesign */}
                    <div className="bg-zinc-700 rounded-t-lg px-3 py-2 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                      </div>
                      <div className="flex-1 bg-zinc-600 rounded px-2 py-0.5 text-zinc-400 text-[10px] truncate">
                        {project.liveUrl ?? 'boostio.sk'}
                      </div>
                    </div>
                    <div className="aspect-video overflow-hidden rounded-b-lg">
                      <BeforeAfterSlider
                        beforeImage={project.beforeImage}
                        afterImage={project.afterImage}
                        beforeLabel="Pred"
                        afterLabel="Po"
                        className="w-full h-full"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="p-3 pt-0">
                    {/* Browser chrome */}
                    <div className="bg-zinc-700 rounded-t-lg px-3 py-2 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                      </div>
                      <div className="flex-1 bg-zinc-600 rounded px-2 py-0.5 text-zinc-400 text-[10px] truncate">
                        {project.liveUrl ?? 'boostio.sk'}
                      </div>
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="nofollow noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 flex items-center justify-center text-zinc-400 hover:text-white transition-colors cursor-pointer"
                        >
                          <i className="ri-external-link-line text-xs" />
                        </a>
                      )}
                    </div>
                    <div className="aspect-video overflow-hidden rounded-b-lg relative bg-zinc-900">
                      <img
                        src={project.image}
                        alt={project.name}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      {project.type === 'redesign' ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-0.5 font-semibold">
                          <i className="ri-refresh-line text-xs" />
                          Redesign
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-orange-400 bg-orange-500/10 border border-orange-500/20 rounded-full px-2.5 py-0.5 font-semibold">
                          <i className="ri-rocket-2-line text-xs" />
                          Od nuly
                        </span>
                      )}
                      <span className="text-xs text-zinc-500 bg-zinc-800 rounded-full px-2.5 py-0.5">
                        {project.category}
                      </span>
                    </div>
                    <h3 className="text-white font-bold text-xl">{project.name}</h3>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-emerald-400 text-sm font-bold">{project.result}</div>
                    <div className="text-zinc-600 text-xs mt-0.5">{project.resultDetail}</div>
                  </div>
                </div>

                <p className="text-zinc-400 text-sm leading-relaxed mb-4">{project.description}</p>

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex flex-wrap gap-1.5">
                    {project.tags.map((tag) => (
                      <span key={tag} className="text-xs text-zinc-500 bg-zinc-800/80 border border-zinc-700/40 rounded px-2 py-0.5">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    {project.liveUrl && (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-external-link-line" />
                        Live ukážka
                      </a>
                    )}
                    {project.caseStudyLink && (
                      <Link
                        to={project.caseStudyLink}
                        className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-400 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-trophy-line" />
                        Case Study
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20 text-zinc-600">
            <i className="ri-search-line text-4xl mb-3 block" />
            <p className="text-sm">Žiadne projekty v tejto kategórii.</p>
          </div>
        )}
      </div>
    </section>
  );
}
